import React, { useState, useEffect } from 'react';
import {
  CheckCircle2, AlertTriangle
} from 'lucide-react';
import { API, apiFetch } from '../lib/api';

function MiniStat({ label, value, highlight, danger }) {
  return (
    <div style={{ padding: 10, background: '#f8fafc', borderRadius: 6, border: `1px solid ${danger ? '#fecaca' : '#e2e8f0'}` }}>
      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: danger ? '#dc2626' : highlight ? '#3b82f6' : '#0f172a' }}>{value ?? '—'}</div>
    </div>
  );
}

// ─── Production ERP — Tier Privilege Integration ─────────────────────────────
export default function ProductionERPIntegrationPanel() {
  const [clientId, setClientId] = useState('');
  const [config, setConfig]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [integrationClients, setIntegrationClients] = useState([]);
  const [statusInfo, setStatusInfo] = useState(null);
  const [syncing, setSyncing]   = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [dbConfig, setDbConfig] = useState({ remoteProdDb: '…', allLocalDbs: [], totalDbs: 0 });

  const endpointUrl = (cid) => `${API}/license/tier-config-export/${cid || '<clientId>'}`;

  useEffect(() => {
    // Load real DB config so the UI shows actual names, not hardcoded strings
    apiFetch(`${API}/integration/config`).then(r => r.json())
      .then(d => setDbConfig(d))
      .catch(() => {});
    apiFetch(`${API}/integration/clients`).then(r => r.json())
      .then(d => Array.isArray(d) ? setIntegrationClients(d) : setIntegrationClients([]))
      .catch(() => {});
  }, []);

  const fetchConfig = async () => {
    if (!clientId.trim()) { setError('Enter a ClientId'); return; }
    setLoading(true);
    setError(null);
    setConfig(null);
    setStatusInfo(null);
    try {
      const res  = await apiFetch(`${API}/license/tier-config-export/${encodeURIComponent(clientId.trim())}`);
      const data = await res.json();
      if (!res.ok || data.error) setError(data.error || `HTTP ${res.status}`);
      else setConfig(data);
      // Also fetch lightweight status pulse
      try {
        const sres = await apiFetch(`${API}/integration/check-status/${encodeURIComponent(clientId.trim())}`);
        const sdata = await sres.json();
        if (sres.ok) setStatusInfo(sdata);
      } catch {}
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const downloadBundle = async () => {
    if (!clientId.trim()) { setError('Enter a ClientId first'); return; }
    try {
      const res  = await apiFetch(`${API}/integration/license-bundle/${encodeURIComponent(clientId.trim())}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `license-bundle-${clientId.trim()}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  const downloadZipBundle = async () => {
    if (!clientId.trim()) { setError('Enter a ClientId first'); return; }
    setError(null);
    try {
      const res = await apiFetch(`${API}/integration/download-bundle/${encodeURIComponent(clientId.trim())}`);
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 120)}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `msn-cliq-erp-bundle-${clientId.trim()}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setError('ZIP bundle download failed: ' + e.message); }
  };

  // ── i18n bundle (translations module — drop-in for any project) ───────────
  // Not personalised per-client; the same ZIP applies to all customers because
  // it reads the central translations registry over HTTP.
  const downloadI18nBundle = async () => {
    setError(null);
    try {
      const res = await apiFetch(`${API}/i18n/bundle/download`);
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 120)}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'msn-cliq-i18n-bundle.zip';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setError('i18n bundle download failed: ' + e.message); }
  };

  const pushToProduction = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res  = await apiFetch(`${API}/integration/sync-tier-modules`, { method: 'POST' });
      const ct   = (res.headers.get('content-type') || '').toLowerCase();
      let data;
      if (ct.includes('application/json')) {
        data = await res.json();
      } else {
        const txt = await res.text();
        // Ingress 502 / proxy timeout — the licensor backend is still pushing.
        if (res.status === 502 || res.status === 504 || /<!DOCTYPE/i.test(txt)) {
          data = {
            success: true,
            async:   true,
            message: 'Push initiated — running on the licensor. Refresh in a moment to see updated counts.'
          };
        } else {
          data = { success: false, error: `HTTP ${res.status}: ${txt.slice(0, 160)}` };
        }
      }
      setSyncResult(data);
    } catch (e) { setSyncResult({ success: false, error: e.message }); }
    setSyncing(false);
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  return (
    <div className="card" style={{ borderTop: '3px solid #3b82f6' }}>
      <div className="card-header">
        <div>
          <div className="card-title">Production ERP — License &amp; Tier Integration</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Endpoints that the live ERP application calls to learn the customer's license, tier, and module privileges.
          </div>
        </div>
        <button
          className="btn btn-primary"
          style={{ fontSize: 12 }}
          onClick={pushToProduction}
          disabled={syncing}
          data-testid="integration-push-prod"
          title={`Force-push tier privileges to ${dbConfig.remoteProdDb} (remote) + ${dbConfig.allLocalDbs?.length || 0} local template/client DBs now`}
        >
          {syncing ? '⟳ Syncing...' : `⇪ Push → ${dbConfig.remoteProdDb} + ${dbConfig.allLocalDbs?.length || 0} Local DBs`}
        </button>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* DB Target Summary */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', background: 'var(--surface-2,#f8fafc)', borderRadius: 6, border: '1px solid var(--border,#e2e8f0)', fontSize: 11 }}>
          <span style={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Push Targets:</span>
          <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>
            {dbConfig.remoteProdDb} <span style={{ fontWeight: 400 }}>(remote)</span>
          </span>
          {dbConfig.allLocalDbs?.map(db => (
            <span key={db} style={{ background: '#dcfce7', color: '#15803d', padding: '1px 7px', borderRadius: 10 }}>{db}</span>
          ))}
          {dbConfig.totalDbs > 0 && (
            <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>{dbConfig.totalDbs} DB(s) total</span>
          )}
        </div>
        {syncResult && (
          <div className={`alert-banner ${syncResult.success ? 'green' : 'red'}`} data-testid="integration-push-result">
            {syncResult.success
              ? <><CheckCircle2 size={14} color="#16a34a" style={{ flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: '#166534' }}>
                    <strong>{syncResult.message}</strong>
                    {syncResult.pushed != null && <> · Remote rows: {syncResult.pushed}</>}
                    {syncResult.localDBs?.ok > 0 && <> · Local DBs pushed: {syncResult.localDBs.ok}</>}
                    {syncResult.localDBs?.errors > 0 && <span style={{ color: '#b45309' }}> · {syncResult.localDBs.errors} local error(s)</span>}
                    {syncResult.remoteError && <span style={{ color: '#b45309' }}> · Remote: {syncResult.remoteError}</span>}
                    {syncResult.async && <> · Still running on the licensor — no error.</>}
                  </div></>
              : <><AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: '#991b1b' }}>Push failed: {syncResult.error}</div></>}
          </div>
        )}

        {/* Quick picker — known licensed clients */}
        {integrationClients.length > 0 && (
          <div>
            <label className="form-label">Quick pick — Licensed Clients ({integrationClients.length})</label>
            <select className="form-select" value={clientId} onChange={e => setClientId(e.target.value)} data-testid="integration-clients-picker">
              <option value="">— Select a licensed client —</option>
              {integrationClients.map(c => (
                <option key={c.clientId} value={c.clientId}>
                  {c.clientName} ({c.clientId}) · {c.tier} · {c.status}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Endpoint URL */}
        <div>
          <label className="form-label">Public endpoint for license check (no auth required)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <code style={{
              flex: 1, padding: '8px 12px', background: '#0f172a', color: '#93c5fd',
              borderRadius: 6, fontFamily: 'monospace', fontSize: 12, overflow: 'auto'
            }} data-testid="prod-erp-endpoint">
              GET {endpointUrl(clientId)}
            </code>
            <button className="btn btn-outline" onClick={() => copy(endpointUrl(clientId))} style={{ fontSize: 12 }}>Copy</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            The live ERP application calls this on login (or every N minutes) to learn which Modules / SubModules / Screens
            the signed-in customer is allowed to use, based on their license tier.
          </div>
        </div>

        {/* Test it */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Test with ClientId</label>
            <input
              className="form-input"
              placeholder="e.g. IN_EXP_0003"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              data-testid="prod-erp-clientid"
            />
          </div>
          <button className="btn btn-primary" onClick={fetchConfig} disabled={loading} data-testid="prod-erp-test">
            {loading ? 'Loading…' : 'Fetch Live Config'}
          </button>
          <button className="btn btn-dark" onClick={downloadBundle} disabled={!clientId.trim()} data-testid="prod-erp-download">
            ⬇ Download Bundle
          </button>
          <button
            className="btn btn-primary"
            style={{ background: '#0f172a', borderColor: '#0f172a' }}
            onClick={downloadZipBundle}
            disabled={!clientId.trim()}
            data-testid="prod-erp-download-zip"
            title="Drop-in standalone ZIP — Express+React security bundle pre-wired for this client"
          >
            ⬇ Download ZIP (Drop-in)
          </button>
          <button
            className="btn btn-outline"
            style={{ borderColor: '#0ea5e9', color: '#0ea5e9' }}
            onClick={downloadI18nBundle}
            data-testid="prod-erp-download-i18n"
            title="Drop-in translations module — vanilla JS + React, multilingual UI for any project"
          >
            ⬇ i18n Bundle ZIP
          </button>
        </div>

        {/* Status info badge */}
        {statusInfo && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }} data-testid="prod-erp-status-info">
            <MiniStat label="Status" value={statusInfo.status} highlight={statusInfo.status === 'Active'} danger={statusInfo.status !== 'Active'} />
            <MiniStat label="Tier" value={statusInfo.tier} highlight />
            <MiniStat label="Days Left" value={statusInfo.daysLeft != null ? String(statusInfo.daysLeft) : '—'} danger={statusInfo.daysLeft != null && statusInfo.daysLeft < 0} />
            <MiniStat label="Module Hash" value={statusInfo.moduleHash} />
            <MiniStat label="Action" value={statusInfo.instruction} danger={statusInfo.instruction === 'BLOCK_ACCESS'} highlight={statusInfo.instruction === 'WARN_RENEWAL'} />
          </div>
        )}

        {/* Result */}
        {error && (
          <div className="alert-banner red">
            <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: '#991b1b' }}>{error}</div>
          </div>
        )}
        {config && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <MiniStat label="Client"      value={config.client?.clientName || config.client?.clientId} />
              <MiniStat label="Tier"        value={config.client?.licenseTier} highlight />
              <MiniStat label="Lic Status"  value={config.client?.licenseStatus || '—'} />
              <MiniStat label="Expired?"    value={config.client?.isExpired ? 'YES' : 'NO'} danger={config.client?.isExpired} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <MiniStat label="Modules"     value={config.privileges?.modules?.length || 0} />
              <MiniStat label="SubModules"  value={config.privileges?.subModules?.length || 0} />
              <MiniStat label="Screens"     value={config.privileges?.screens?.length || 0} />
            </div>
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: '#3b82f6', fontWeight: 700 }}>View raw JSON response</summary>
              <pre style={{
                background: '#0f172a', color: '#93c5fd', padding: 14, borderRadius: 6,
                fontFamily: 'monospace', fontSize: 12, marginTop: 8, maxHeight: 320, overflow: 'auto'
              }}>{JSON.stringify(config, null, 2)}</pre>
            </details>
          </div>
        )}

        {/* Integration snippet */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
            Drop into your production ERP application
          </div>
          <pre style={{
            background: '#0f172a', color: '#e2e8f0', padding: 14, borderRadius: 6,
            fontFamily: 'monospace', fontSize: 12, margin: 0, overflow: 'auto', lineHeight: 1.6
          }}>{`// On user login / app boot
const r = await fetch(API + '/license/tier-config-export/' + clientId);
const cfg = await r.json();
// cfg.privileges.modules    => INT[] of allowed ModuleIds
// cfg.privileges.subModules => STRING[] of allowed SubModuleIds (e.g. "1.10")
// cfg.privileges.screens    => INT[] of allowed Screen SeqIds
// cfg.client.licenseTier    => current tier
// cfg.client.isExpired      => true / false
hidePages(cfg.privileges.screens);`}</pre>
        </div>
      </div>
    </div>
  );
}
